import { LocalStorageService } from '../../services/local-storage.service';
import { Injectable, OnDestroy } from '@angular/core';

export type MapShortcutAction =
  | 'temporaryPan'
  | 'navigate';

export interface MapShortcutBindings {
  readonly temporaryPan: string;
  readonly navigate: string;
}

const STORAGE_KEY = 'map-shortcuts@1';
const DEFAULT_BINDINGS: MapShortcutBindings = {
  temporaryPan: 'Space',
  navigate: 'Escape',
};

/** Owns user-configurable map key bindings; it never registers DOM listeners. */
@Injectable({ providedIn: 'root' })
export class MapShortcutService implements OnDestroy {
  private readonly sessionSubscription;
  constructor(private readonly storage: LocalStorageService) {
    this.sessionSubscription = storage.sessionChanged$.subscribe(() => {
      this.bindings = this.read();
    });
  }

  ngOnDestroy(): void { this.sessionSubscription.unsubscribe(); }
  private bindings = this.read();

  binding(action: MapShortcutAction): string {
    return this.bindings[action];
  }

  matches(event: KeyboardEvent, action: MapShortcutAction): boolean {
    return event.code === this.binding(action);
  }

  setBinding(action: MapShortcutAction, code: string): void {
    this.bindings = { ...this.bindings, [action]: code };
    this.storage.set(STORAGE_KEY, this.bindings);
  }

  private read(): MapShortcutBindings {
    try {
      const stored = this.storage.get<Partial<MapShortcutBindings>>(STORAGE_KEY) ?? {};
      return {
        temporaryPan: typeof stored.temporaryPan === 'string'
          ? stored.temporaryPan
          : DEFAULT_BINDINGS.temporaryPan,
        navigate: typeof stored.navigate === 'string'
          ? stored.navigate
          : DEFAULT_BINDINGS.navigate,
      };
    } catch {
      return DEFAULT_BINDINGS;
    }
  }
}
